import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';
import { Maximize, BedDouble, Bath, Zap, FileText, Phone, MessageCircle, Mail, Lock, Check } from 'lucide-react';
import { usePropertyPreview } from '../../hooks/usePropertyPreview';
import { useSettings } from '../../hooks/useSettings';
import { getWatermarkedImageUrl, PLACEHOLDER_IMAGE } from '../../utils/imageProxy';
import { getPropertyPriceLabel, hasPublicPrice } from '../../utils/propertyPrice';
import { getHeatingLabel } from '../../utils/heating';
import PropertyFloorplan from '../PropertyDetail/components/PropertyFloorplan';

/**
 * Pagina del link privato.
 *
 * Vive fuori da MainLayout: niente menu, niente footer del sito, niente
 * ricerca, niente immobili correlati, nessun link verso il resto del dominio.
 * Chi riceve il link vede questa scheda e basta.
 */
const PropertyPreview: React.FC = () => {
  const { token } = useParams();
  const { property, state } = usePropertyPreview(token || '');
  const { settings } = useSettings();
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [floorplanOpen, setFloorplanOpen] = useState(false);

  const robots = (
    <Helmet>
      <meta name="robots" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
      <meta name="googlebot" content="noindex, nofollow" />
      <meta name="referrer" content="no-referrer" />
    </Helmet>
  );

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        {robots}
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-r-transparent" />
      </div>
    );
  }

  if (state !== 'ok' || !property) {
    // Testo di cortesia per link revocato, scaduto o inesistente.
    // Volutamente non dice se il link è mai esistito.
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        {robots}
        <div className="text-center max-w-sm">
          <Lock className="h-10 w-10 text-primary mx-auto mb-5" />
          <h1 className="text-2xl font-monomakh text-primary mb-3">Questo link non è più attivo</h1>
          <p className="text-gray-600 mb-6">
            La scheda che stai cercando non è più disponibile a questo indirizzo.
            Se ti serve, chiedila a chi te l'ha mandata.
          </p>
          {settings?.phone && (
            <a href={`tel:${settings.phone.replace(/\s/g, '')}`} className="text-secondary font-medium">
              {settings.phone}
            </a>
          )}
        </div>
      </div>
    );
  }

  const images: string[] = property.images.length ? property.images : [PLACEHOLDER_IMAGE];
  const heroImage = getWatermarkedImageUrl(images[0], 1600);
  const zona = property.zones?.name || property.location;

  const datiEssenziali = [
    { icon: Maximize, label: 'Superficie', value: property.size ? `${property.size} m²` : null },
    {
      icon: BedDouble,
      label: property.total_rooms === 1 ? 'Locale' : 'Locali',
      value: property.total_rooms || property.bedrooms || null,
    },
    {
      icon: Bath,
      label: property.bathrooms === 1 ? 'Bagno' : 'Bagni',
      value: property.bathrooms || null,
    },
    { icon: Zap, label: 'Classe', value: property.energy_class || null },
  ].filter(dato => dato.value !== null && dato.value !== undefined);

  const dettagli = [
    { label: 'Tipologia', value: property.property_types?.name },
    { label: 'Stato', value: property.property_states?.name },
    { label: 'Riscaldamento', value: getHeatingLabel(property) },
    { label: 'Spese', value: property.extra_costs },
    { label: 'Riferimento', value: property.reference_number },
  ].filter(dettaglio => dettaglio.value);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{property.title}</title>
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
        <meta name="googlebot" content="noindex, nofollow" />
        <meta name="referrer" content="no-referrer" />
      </Helmet>

      {/* Intestazione ridotta: identifica l'agenzia, non porta da nessuna parte */}
      <header className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <img src="/logo_molinaro.png" alt="Molinaro Immobiliare" className="h-10" />
        <span className="text-[11px] uppercase tracking-wider text-gray-400">Scheda riservata</span>
      </header>

      {/* Foto principale a tutta larghezza */}
      <button
        type="button"
        onClick={() => setLightboxIndex(0)}
        className="block w-full"
        aria-label="Apri la galleria fotografica"
      >
        <img
          src={heroImage}
          alt={property.title}
          className="w-full h-[52vh] min-h-[260px] max-h-[560px] object-cover"
        />
      </button>

      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto px-3 py-3 bg-gray-50">
          {images.map((url, index) => (
            <button
              key={`${url}-${index}`}
              type="button"
              onClick={() => setLightboxIndex(index)}
              className="flex-shrink-0"
              aria-label={`Foto ${index + 1}`}
            >
              <img
                src={getWatermarkedImageUrl(url, 320)}
                alt=""
                className="h-16 w-24 object-cover rounded"
              />
            </button>
          ))}
        </div>
      )}

      <main className="max-w-2xl mx-auto px-5 py-8">
        <h1 className="text-3xl font-monomakh text-primary leading-tight">{property.title}</h1>
        {zona && <p className="text-gray-500 mt-1">{zona}</p>}

        <div className="mt-5 pb-6 border-b border-gray-100">
          <div className="text-3xl font-bold text-primary">{getPropertyPriceLabel(property)}</div>
          {hasPublicPrice(property) && property.type === 'affitto' && <div className="text-sm text-gray-500">al mese</div>}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-6 border-b border-gray-100">
          {datiEssenziali.map(dato => (
            <div key={dato.label} className="text-center">
              <dato.icon className="h-5 w-5 text-secondary mx-auto mb-2" />
              <div className="font-semibold text-primary">{dato.value}</div>
              <div className="text-xs text-gray-500">{dato.label}</div>
            </div>
          ))}
        </div>

        {property.description && (
          <section className="py-6 border-b border-gray-100">
            <div
              className="prose prose-sm max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: property.description }}
            />
          </section>
        )}

        {property.strengths.length > 0 && (
          <section className="py-6 border-b border-gray-100">
            <h2 className="text-sm uppercase tracking-wider text-gray-400 mb-4">Punti di forza</h2>
            <ul className="space-y-2">
              {property.strengths.map((strength: any, index: number) => (
                <li key={strength.id || index} className="flex gap-2 text-gray-700">
                  <Check className="h-4 w-4 text-secondary flex-shrink-0 mt-1" />
                  <span>{strength.description}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {dettagli.length > 0 && (
          <section className="py-6 border-b border-gray-100">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {dettagli.map(dettaglio => (
                <div key={dettaglio.label}>
                  <dt className="text-gray-400">{dettaglio.label}</dt>
                  <dd className="text-gray-800">{dettaglio.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {property.features.length > 0 && (
          <section className="py-6 border-b border-gray-100">
            <h2 className="text-sm uppercase tracking-wider text-gray-400 mb-4">Caratteristiche</h2>
            <div className="flex flex-wrap gap-2">
              {property.features.map((feature: any) => (
                <span key={feature.id} className="text-sm bg-gray-100 text-gray-700 rounded-full px-3 py-1">
                  {feature.name}
                </span>
              ))}
            </div>
          </section>
        )}

        {property.planimetria && (
          <section className="py-6 border-b border-gray-100">
            <button
              type="button"
              onClick={() => setFloorplanOpen(true)}
              className="w-full flex items-center justify-center gap-2 border border-primary text-primary rounded-lg py-3 hover:bg-primary hover:text-white transition-colors"
            >
              <FileText className="h-5 w-5" />
              Vedi la planimetria
            </button>
          </section>
        )}

        <section className="py-8 text-center">
          <p className="text-gray-500 text-sm mb-5">Per qualsiasi domanda su questo immobile</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {settings?.phone && (
              <a
                href={`tel:${settings.phone.replace(/\s/g, '')}`}
                className="flex items-center justify-center gap-2 bg-primary text-white rounded-lg px-5 py-3"
              >
                <Phone className="h-4 w-4" />
                {settings.phone}
              </a>
            )}
            {settings?.whatsapp && (
              <a
                href={`https://wa.me/${settings.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center justify-center gap-2 border border-gray-200 text-gray-700 rounded-lg px-5 py-3"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp
              </a>
            )}
            {settings?.email && (
              <a
                href={`mailto:${settings.email}`}
                className="flex items-center justify-center gap-2 border border-gray-200 text-gray-700 rounded-lg px-5 py-3"
              >
                <Mail className="h-4 w-4" />
                Scrivici
              </a>
            )}
          </div>
        </section>

        <p className="text-center text-xs text-gray-400 pb-8">
          Documento riservato al destinatario. Dati e immagini non costituiscono proposta contrattuale.
        </p>
      </main>

      <Lightbox
        open={lightboxIndex >= 0}
        index={lightboxIndex < 0 ? 0 : lightboxIndex}
        close={() => setLightboxIndex(-1)}
        slides={images.map(url => ({ src: getWatermarkedImageUrl(url, 1600) }))}
        plugins={[Zoom, Counter]}
      />

      <PropertyFloorplan
        planimetriaUrl={property.planimetria}
        propertyName={property.title}
        isOpen={floorplanOpen}
        onClose={() => setFloorplanOpen(false)}
      />
    </div>
  );
};

export default PropertyPreview;
